/* eslint-disable */
import { BehaviorSubject, Subject } from "rxjs";
import jwt_decode from "jwt-decode";
import { get } from "../../../http/shared/methods/get";
import { put, post, OK_RESPONSE, NO_CONTENT_RESPONSE, postFile, BAD_REQUEST } from "../../../http";
import { isAuth, getToken } from "../../../auth/shared/utils/auth";

export type User = {
  name: string;
  surname: string;
  departmentId: number;
  profilePicture: string;
  email?: string;
  migrationPasswordValid?: boolean;
};

export type PasswordData = {
  currentPassword: string;
  newPassword: string;
  user?: string;
};

export enum UserError {
  Network,
  Server,
  InvalidCredentials,
  NotVerified,
}

export enum RequestStatus {
  None,
  Pending,
  Success,
  Fail,
}

const USER_ROLE_KEY = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";
export class UserService {
  private _baseUrl: string;
  private _fileSendUrl: string;
  private _isAdmin$ = new BehaviorSubject<boolean>(false);
  private _user$ = new BehaviorSubject<User>({
    name: "",
    surname: "",
    departmentId: 1,
    profilePicture: "",
  });
  private _error$ = new Subject<UserError>();
  private _loading$ = new BehaviorSubject<boolean>(false);
  private _status$ = new BehaviorSubject<RequestStatus>(RequestStatus.None);

  public get error$() {
    return this._error$.asObservable();
  }

  public get user$() {
    return this._user$.asObservable();
  }

  public get status$() {
    return this._status$.asObservable();
  }

  public get isAdmin$() {
    return this._isAdmin$.asObservable();
  }

  public get user() {
    return this._user$.value;
  }

  public get loading$() {
    return this._loading$.asObservable();
  }

  public get loading() {
    return this._loading$.value;
  }

  constructor(apiUrl: string) {
    this._fileSendUrl = apiUrl + "/FilesUpload";
    this._baseUrl = apiUrl + "/UserProfile";
  }

  // TODO Вынести в глобальный метод добавление заголовка
  addAuthInHeadersOption = () => {
    let options = {};
    if (isAuth()) {
      const token = getToken();
      options = {
        headers: {
          Authorization: `Bearer ${token}`,
          Pragma: "no-cache",
        },
      };
    }
    return options;
  };

  checkAdminStatus = () => {
    if (isAuth()) {
      const token = getToken();
      const decoded = jwt_decode<Record<string, unknown>>(token as string);
      this._isAdmin$.next(Boolean(decoded[USER_ROLE_KEY] && decoded[USER_ROLE_KEY] === "admin"));
    }
  };

  async getUserInfo() {
    try {
      this._loading$.next(true);
      const options = this.addAuthInHeadersOption();
      const result = await get(`${this._baseUrl}`, undefined, options);
      result.json().then((res) => {
        this._user$.next(res);
      });

      this._loading$.next(false);
    } catch (err) {
      this._loading$.next(false);

      // TODO: Добавить логику обработки ошибок
      // eslint-disable-next-line no-console
      console.error("get user info fail");
      // eslint-disable-next-line no-console
      console.error(err);
      throw err;
    }
  }

  async saveUserInfo(userInfo: User, file?: File | Blob) {
    try {
      this._loading$.next(true);
      this._status$.next(RequestStatus.Pending);
      const options = this.addAuthInHeadersOption();
      const payload: Partial<User> = {
        name: userInfo.name,
        surname: userInfo.surname,
        departmentId: userInfo.departmentId,
        profilePicture: userInfo.profilePicture,
        migrationPasswordValid: userInfo.migrationPasswordValid,
      };
      let imageUrl = "";
      if (file) {
        const body = new FormData();
        body.append("file", file as Blob);

        imageUrl = await postFile(this._fileSendUrl, file).then((res) => (res.ok ? res.text() : ""));
        if (imageUrl) {
          payload["profilePicture"] = imageUrl.slice(1); //TODO: fix url
        }
      }
      await put(`${this._baseUrl}`, payload, options);
      this._user$.next(userInfo);
      this._loading$.next(false);
      this._status$.next(RequestStatus.None);
      this.getUserInfo();
    } catch (err) {
      this._loading$.next(false);

      // TODO: Добавить логику обработки ошибок
      // eslint-disable-next-line no-console
      console.error("save user info fail");
      // eslint-disable-next-line no-console
      console.error(err);
      throw err;
    }
  }

  async changeUserPassword(passwordData: PasswordData) {
    try {
      this._loading$.next(true);
      this._status$.next(RequestStatus.Pending);
      const options = this.addAuthInHeadersOption();
      const result = await post(`${this._baseUrl}/changepassword`, passwordData, options);

      if (result.status === BAD_REQUEST){
        this._status$.next(RequestStatus.Fail);
      }
      if (result.status === OK_RESPONSE || result.status === NO_CONTENT_RESPONSE) {
        this._status$.next(RequestStatus.Success);
        this._user$.next({
          ...this._user$.value,
          migrationPasswordValid: true,
        });
      }
      this._loading$.next(false);
    } catch (err) {
      this._status$.next(RequestStatus.Fail);
      this._loading$.next(false);

      // TODO: Добавить логику обработки ошибок
      // eslint-disable-next-line no-console
      console.error("change user password fail");
      // eslint-disable-next-line no-console
      console.error(err);
      throw err;
    }
  }
}
